import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { MenuGroup, MenuItem } from '@nexds/web'

import { InjectionTokens } from '@/controller/tokens'

import { MapLayerState, MapLayerStateProps } from '@/domain/states/MapLayerState'

import { ToolContainer } from '../ToolbarTools.styles'
import { LegendsContainer } from './ADHPTool.styles'
import { Drawer } from './Drawer'
import { Legend } from './Legend'
import { Slider } from './Slider'

import { MetricsEvents } from '@/constants/metricsEvents'
import { useBehaviorSubject } from '@/presentation/hooks/useBehaviorSubject'
import { useGlobalState } from '@/presentation/hooks/useGlobalState'
import { useI18n } from '@/presentation/hooks/useI18n'
import icons from '@/presentation/modules/MapScreen/components/MapADHP/icons'
import { navaidIcons } from '@/presentation/modules/MapScreen/components/MapNavaid/icons'
import { obstacleIcons } from '@/presentation/modules/MapScreen/components/MapObstacles/icons'
import { FuelStateMutator } from '@/presentation/modules/MapScreen/states/FuelState'
import {
  MapADHPState,
  MapADHPStateMutator,
  MapADHPStateProps
} from '@/presentation/modules/MapScreen/states/MapADHPState'
import { sendMetrics } from '@/presentation/utils/sendMetrics'

function ADHPTool() {
  const { t } = useI18n()

  const mapADHPState = useBehaviorSubject<MapADHPStateProps>(MapADHPState)
  const [mapLayersState, mapLayersStateW] = useGlobalState<MapLayerState, MapLayerStateProps>(
    InjectionTokens.MapLayerState
  )

  const [filtersOpen, setFiltersOpen] = useState(false)
  const [legendsOpen, setLegendsOpen] = useState(false)
  const isFirstRender = useRef(true)

  useEffect(() => {
    if (filtersOpen) {
      setLegendsOpen(false)
    }
  }, [filtersOpen])

  useEffect(() => {
    if (legendsOpen) {
      setFiltersOpen(false)
      sendMetrics(MetricsEvents.TOOLBAR_ADHP_LEGEND_CLICKED)
    }
  }, [legendsOpen])

  useLayoutEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      const adhpState = mapLayersStateW.getAllADHP()
      MapADHPStateMutator.mutate({
        ...adhpState
      })
      return
    }

    return () => {
      const adhpStateToPersist = MapADHPState.getValue()
      mapLayersStateW.setAllADHP({
        ...adhpStateToPersist
      })
    }
  }, [isFirstRender.current])

  type LegendItem = { icon: string; meaning: string; isNavaid?: boolean }

  const aerodromeLegends: LegendItem[] = [
    { icon: icons['legend-aerodrome-paved'], meaning: t('TOOLBAR_MAP-POINTS_LEGENDS_ITEM_PAVED-AERODROME') },
    {
      icon: icons['legend-military-aerodrome-paved'],
      meaning: t('TOOLBAR_MAP-POINTS_LEGENDS_ITEM_PAVED-MILITARY-AERODROME')
    },
    { icon: icons['legend-aerodrome-unpaved'], meaning: t('TOOLBAR_MAP-POINTS_LEGENDS_ITEM_UNPAVED-AERODROME') },
    {
      icon: icons['legend-military-aerodrome-unpaved'],
      meaning: t('TOOLBAR_MAP-POINTS_LEGENDS_ITEM_UNPAVED-MILITARY-AERODROME')
    }
  ]

  const helipadLegends: LegendItem[] = [
    { icon: icons['legend-helipad'], meaning: t('TOOLBAR_MAP-POINTS_LEGENDS_ITEM_HELIPAD') },
    { icon: icons['legend-military-helipad'], meaning: t('TOOLBAR_MAP-POINTS_LEGENDS_ITEM_MILITARY-HELIPAD') }
  ]

  const userWaypointLegends: LegendItem[] = [
    { icon: icons['legend-user-waypoint'], meaning: t('TOOLBAR_MAP-POINTS_LEGENDS_ITEM_USER-WAYPOINT') }
  ]

  const obstacleLegends: LegendItem[] = [
    { icon: obstacleIcons['obstacle-low'], meaning: t('TOOLBAR_MAP-POINTS_LEGENDS_ITEM_LOW-OBSTACLE') },
    {
      icon: obstacleIcons['obstacle-low-lighted'],
      meaning: t('TOOLBAR_MAP-POINTS_LEGENDS_ITEM_LIGHTED-LOW-OBSTACLE')
    },
    { icon: obstacleIcons['obstacle-high'], meaning: t('TOOLBAR_MAP-POINTS_LEGENDS_ITEM_HIGH-OBSTACLE') },
    {
      icon: obstacleIcons['obstacle-high-lighted'],
      meaning: t('TOOLBAR_MAP-POINTS_LEGENDS_ITEM_LIGHTED-HIGH-OBSTACLE')
    },
    { icon: obstacleIcons['obstacle-cluster'], meaning: t('TOOLBAR_MAP-POINTS_LEGENDS_ITEM_MULTIPLE-OBSTACLES') }
  ]

  const navaidLegends: LegendItem[] = [
    { icon: navaidIcons['ndb'], meaning: t('TOOLBAR_MAP-POINTS_LEGENDS_ITEM_NDB'), isNavaid: true },
    { icon: navaidIcons['vor'], meaning: t('TOOLBAR_MAP-POINTS_LEGENDS_ITEM_VOR'), isNavaid: true },
    { icon: navaidIcons['vor-dme'], meaning: t('TOOLBAR_MAP-POINTS_LEGENDS_ITEM_VOR-DME'), isNavaid: true },
    { icon: navaidIcons['dme'], meaning: t('TOOLBAR_MAP-POINTS_LEGENDS_ITEM_DME'), isNavaid: true }
  ]

  const legends: LegendItem[] = [
    ...(mapADHPState.aerodromes ? aerodromeLegends : []),
    ...(mapADHPState.helipads ? helipadLegends : []),
    ...(mapLayersState.navaids ? navaidLegends : []),
    ...(mapADHPState.userWaypoints ? userWaypointLegends : []),
    ...(mapLayersState.obstacles ? obstacleLegends : [])
  ]

  return (
    <ToolContainer gap={16}>
      <MenuGroup>
        <MenuItem
          title={t('TOOLBAR_MAP-POINTS_ITEM_AERODROMES_LABEL')}
          size="sm"
          hasSwitch
          isSwitchChecked={mapADHPState.aerodromes}
          onClick={() => {
            MapADHPStateMutator.setAerodromes(!mapADHPState.aerodromes)
            FuelStateMutator.setLayer(null)
            sendMetrics(MetricsEvents.TOOLBAR_ADHP_SELECTED, {
              extra: 'aerodrome',
              active: !mapADHPState.aerodromes
            })
          }}
        />
        <Drawer isOpen={filtersOpen} setIsOpen={setFiltersOpen} handlePosition="bottom" title="Filtros">
          <Slider
            label={t('TOOLBAR_MAP-POINTS_ITEM_RUNWAY-LENGTH_LABEL')}
            id="min-runway-length"
            max={2500}
            min={0}
            step={100}
            value={mapADHPState.minRunwayLength}
            formatedValue={`${mapADHPState.minRunwayLength?.toString().padStart(4, '0') || '0000'} m`}
            onChange={(e) => {
              MapADHPStateMutator.setMinRunwayLength(parseInt(e.target.value))
            }}
            onDragExit={() => {
              sendMetrics(MetricsEvents.TOOLBAR_ADHP_MIN_TRACK_LENGTH_CLICKED)
            }}
          />

          <MenuGroup>
            <MenuItem
              title={t('TOOLBAR_MAP-POINTS_ITEM_PAVED-RUNWAY_LABEL')}
              size="sm"
              hasSwitch
              isSwitchChecked={mapADHPState.pavedRunway}
              onClick={() => {
                MapADHPStateMutator.setPavedRunway(!mapADHPState.pavedRunway)
                FuelStateMutator.setLayer(null)
                sendMetrics(MetricsEvents.TOOLBAR_ADHP_FILTER_SELECTED, {
                  extra: 'paved runway',
                  active: !mapADHPState.pavedRunway
                })
              }}
            />

            <MenuItem
              title={t('TOOLBAR_MAP-POINTS_ITEM_NIGHT-OPERATION_LABEL')}
              size="sm"
              hasSwitch
              isSwitchChecked={mapADHPState.nightOperation}
              onClick={() => {
                MapADHPStateMutator.setNightOperation(!mapADHPState.nightOperation)
                FuelStateMutator.setLayer(null)
                sendMetrics(MetricsEvents.TOOLBAR_ADHP_FILTER_SELECTED, {
                  extra: 'night operation',
                  active: !mapADHPState.nightOperation
                })
              }}
            />

            <MenuItem
              title={t('TOOLBAR_MAP-POINTS_ITEM_ATS_LABEL')}
              size="sm"
              hasSwitch
              isSwitchChecked={mapADHPState.ats}
              onClick={() => {
                MapADHPStateMutator.setAts(!mapADHPState.ats)
                FuelStateMutator.setLayer(null)
                sendMetrics(MetricsEvents.TOOLBAR_ADHP_FILTER_SELECTED, {
                  extra: 'ats',
                  active: !mapADHPState.ats
                })
              }}
            />

            <MenuItem
              title={t('TOOLBAR_MAP-POINTS_ITEM_IFR_LABEL')}
              size="sm"
              hasSwitch
              isSwitchChecked={mapADHPState.ifr}
              onClick={() => {
                MapADHPStateMutator.setIfr(!mapADHPState.ifr)
                FuelStateMutator.setLayer(null)
                sendMetrics(MetricsEvents.TOOLBAR_ADHP_FILTER_SELECTED, {
                  extra: 'ifr',
                  active: !mapADHPState.ifr
                })
              }}
            />
          </MenuGroup>
        </Drawer>
      </MenuGroup>

      <MenuItem
        title={t('TOOLBAR_MAP-POINTS_ITEM_HELIPADS_LABEL')}
        size="sm"
        hasSwitch
        isSwitchChecked={mapADHPState.helipads}
        onClick={() => {
          MapADHPStateMutator.setHelipads(!mapADHPState.helipads)
          FuelStateMutator.setLayer(null)
          sendMetrics(MetricsEvents.TOOLBAR_ADHP_SELECTED, {
            extra: 'helipad',
            active: !mapADHPState.helipads
          })
        }}
      />

      <MenuItem
        title={t('TOOLBAR_MAP-POINTS_ITEM_NAVAIDS_LABEL')}
        size="sm"
        hasSwitch
        isSwitchChecked={mapLayersState.navaids}
        onClick={() => {
          const active = !mapLayersState.navaids
          mapLayersStateW.setNavaids(active)
          sendMetrics(MetricsEvents.TOOLBAR_NAVAIDS_SELECTED, { active })
        }}
      />

      <MenuItem
        title={t('TOOLBAR_MAP-POINTS_ITEM_USER-WAYPOINTS_LABEL')}
        size="sm"
        hasSwitch
        isSwitchChecked={mapADHPState.userWaypoints}
        onClick={() => {
          MapADHPStateMutator.setUserWaypoints(!mapADHPState.userWaypoints)
          FuelStateMutator.setLayer(null)
          sendMetrics(MetricsEvents.TOOLBAR_ADHP_USER_WAYPOINTS_SELECTED, {
            active: !mapADHPState.userWaypoints
          })
        }}
      />

      <MenuItem
        title={t('TOOLBAR_MAP-POINTS_ITEM_OBSTACLES_LABEL')}
        size="sm"
        hasSwitch
        isSwitchChecked={mapLayersState.obstacles}
        onClick={() => {
          const active = !mapLayersState.obstacles
          mapLayersStateW.setObstacles(active)
          sendMetrics(MetricsEvents.TOOLBAR_OBSTACLES_SELECTED, { active })
        }}
      />

      {legends.length > 0 && (
        <Drawer
          isOpen={legendsOpen}
          setIsOpen={setLegendsOpen}
          title={t('TOOLBAR_MAP-POINTS_ITEM_LEGENDS_LABEL')}
          lastChild
        >
          <LegendsContainer>
            {legends.map((legend, place) => (
              <Legend
                key={legend.meaning}
                icon={legend.icon}
                meaning={legend.meaning}
                legendsLength={legends.length}
                place={place}
                isNavaid={legend.isNavaid}
              />
            ))}
          </LegendsContainer>
        </Drawer>
      )}
    </ToolContainer>
  )
}

export { ADHPTool }
